import React from 'react';
import { TextField as PloneTextField } from '@plone/components';
import { clsx } from 'clsx';
import './TextField.scss';

export interface ESSTextFieldProps {
  className?: string;
  /** Label for the text field */
  label?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Whether the input is in an error state */
  isInvalid?: boolean;
  /** Error message to display when isInvalid is true */
  errorMessage?: string;
  /** Icon to display at the start of the input */
  startIcon?: React.ReactNode;
  /** Icon to display at the end of the input */
  endIcon?: React.ReactNode;
  /** Size of the text field */
  size?: 'sm' | 'md' | 'lg';
  /** Field name */
  name?: string;
  /** Field type */
  type?: string;
  /** Field value */
  value?: string;
  /** Border radius degree (uses token value, e.g., 0 for none) */
  borderRadius?: string | number;
  /** Change handler - receives the new value directly */
  onChange?: (value: string) => void;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Allow any other props */
  [key: string]: any;
}

export const TextField: React.FC<ESSTextFieldProps> = ({
  label,
  placeholder,
  helperText,
  isInvalid,
  errorMessage,
  startIcon,
  endIcon,
  size = 'md',
  borderRadius,
  className,
  onChange,
  ...props
}) => {
  // Wrap onChange to handle @plone/components API (value: string)
  const handleChange = onChange
    ? (value: string) => {
        onChange(value);
      }
    : undefined;

  const fieldClasses = clsx(
    'ess-textfield',
    `ess-textfield--${size}`,
    {
      'ess-textfield--invalid': isInvalid,
      'ess-textfield--no-label': !label,
    },
    className,
  );

  var bRadius, paddingX, paddingY, fontSize;
  if (size === 'sm') {
    fontSize = 'var(--ess-font-size-sm)';
    paddingX = 'var(--ess-space-xxs)';
    paddingY = 'var(--ess-space-xxs)';
    if (borderRadius == undefined) {
      bRadius = 'var(--input-variantdefault-sm-statedefault-radius)';
    }
  } else if (size === 'md') {
    fontSize = 'var(--ess-font-size-base)';
    paddingX = 'var(--ess-space-2xl)';
    paddingY = 'var(--ess-space-sm)';
    if (borderRadius == undefined) {
      bRadius = 'var(--input-variantdefault-md-statedefault-radius)';
    }
  } else if (size === 'lg') {
    fontSize = 'var(--ess-font-size-lg)';
    paddingX = 'var(--ess-space-3xl)';
    paddingY = 'var(--ess-space-base)';
  }

  // Build inline styles for custom colors and border radius
  const inputStyles = {
    '--textfield-radius': bRadius,
    width: '100%',
    maxWidth: '100%',
  };

  const inputWrapperStyles = {
    '--textfield-padding-y': paddingY,
    '--textfield-padding-left': startIcon ? 'var(--ess-space-3xl)' : paddingX,
    '--textfield-padding-right': endIcon ? 'var(--ess-space-3xl)' : paddingX,
    '--textfield-font-size': fontSize,
    width: '100%',
    maxWidth: '100%',
    display: 'block',
  };

  return (
    <div className={fieldClasses} style={inputWrapperStyles}>
      {label && (
        <span className="ess-textfield__label" aria-hidden="true">
          {label}
        </span>
      )}
      <div className="ess-textfield__input-wrapper">
        {startIcon && (
          <span className="ess-textfield__start-icon" aria-hidden="true">
            {startIcon}
          </span>
        )}
        <PloneTextField
          aria-label={label}
          placeholder={placeholder}
          isInvalid={isInvalid}
          onChange={handleChange}
          className="ess-textfield__field"
          style={inputStyles}
          {...props}
        />
        {endIcon && (
          <span className="ess-textfield__end-icon" aria-hidden="true">
            {endIcon}
          </span>
        )}
      </div>
      {isInvalid && errorMessage && (
        <div className="ess-textfield__error-text" role="alert">
          {errorMessage}
        </div>
      )}
      {helperText && !isInvalid && (
        <div className="ess-textfield__help-text">{helperText}</div>
      )}
    </div>
  );
};

TextField.displayName = 'ESSTextField';
